import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import backend from "../../backend";

/**
 * FacturarGuiaCompletaModal — promueve TODAS las deliveries de una guía
 * a factura/boleta en un solo flujo. Reusa el endpoint existente
 * `PATCH /workOrder/:id/delivery/:deliveryId` iterando secuencialmente.
 *
 * Props:
 *   target  {
 *     guiaNumber, guiaDate,
 *     coNumber, clientName, monto,
 *     deliveries: [{ workOrderId, deliveryId, qty, itemLabel }]
 *   } | null
 *   onClose, onSaved
 */
const fmtCLP = (n) => new Intl.NumberFormat("es-CL", {
  style: "currency", currency: "CLP", maximumFractionDigits: 0,
}).format(n || 0);

const FacturarGuiaCompletaModal = ({ target, onClose, onSaved }) => {
  const [docType, setDocType]     = useState("factura");
  const [docNumber, setDocNumber] = useState("");
  const [docDate, setDocDate]     = useState("");
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");
  const [progress, setProgress]   = useState(null); // { done, total }

  useEffect(() => {
    if (!target) return;
    setDocType("factura");
    setDocNumber("");
    setDocDate(new Date().toISOString().slice(0, 10));
    setError("");
    setProgress(null);
  }, [target]);

  if (!target) return null;
  const deliveries = target.deliveries || [];

  const submit = async () => {
    setError("");
    if (!docNumber.trim()) { setError("N° de factura requerido"); return; }
    if (!deliveries.length) { setError("La guía no tiene entregas pendientes"); return; }
    setSaving(true);
    setProgress({ done: 0, total: deliveries.length });
    try {
      const isoDate = docDate ? new Date(docDate).toISOString() : new Date().toISOString();
      const docN    = docNumber.trim();
      for (let i = 0; i < deliveries.length; i++) {
        const d = deliveries[i];
        await backend.patch(
          `workOrder/${d.workOrderId}/delivery/${d.deliveryId}`,
          { docType, docNumber: docN, docDate: isoDate }
        );
        setProgress({ done: i + 1, total: deliveries.length });
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(
        (e?.response?.data?.error || e.message || "Error al facturar guía")
        + (progress ? ` (procesadas ${progress.done}/${progress.total})` : "")
      );
    }
    setSaving(false);
  };

  return ReactDOM.createPortal(
    <div
      onMouseDown={e => { if (e.target === e.currentTarget && !saving) onClose?.(); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 10001,
      }}
    >
      <div style={{
        background: "#1a1a1a", border: "1px solid #333", borderRadius: 8,
        width: "min(520px, 92vw)", padding: "18px 22px", color: "#ddd",
        fontSize: 12, fontFamily: "Arial, sans-serif",
      }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#fff" }}>
            Facturar guía completa
          </div>
          <button
            onClick={() => !saving && onClose?.()}
            style={{ background:"transparent", border:"none", color: "#cbd5e1", fontSize:18, cursor:"pointer", lineHeight:1 }}
          >×</button>
        </div>

        <div style={{
          background:"#161616", border:"1px solid #2e2e2e", borderRadius: 4,
          padding: "10px 12px", marginBottom: 14, fontSize: 11, lineHeight: 1.5,
        }}>
          <div style={{ color: "#cbd5e1", fontSize: 10, textTransform:"uppercase", letterSpacing: 0.5 }}>
            Guía a facturar
          </div>
          <div style={{ color:"#fff", marginTop: 4 }}>
            📄 Guía <strong style={{ color:"#3498db" }}>{target.guiaNumber}</strong>
            {target.guiaDate && (
              <span style={{ color: "#aaa", marginLeft: 8 }}>
                emitida {new Date(target.guiaDate).toLocaleDateString("es-CL")}
              </span>
            )}
          </div>
          <div style={{ color: "#aaa", marginTop: 4 }}>
            {deliveries.length} ítem{deliveries.length === 1 ? "" : "s"}
            {target.coNumber && <span> · OC interna #{target.coNumber}</span>}
            {target.clientName && <span> · {target.clientName}</span>}
            {target.monto != null && <span> · {fmtCLP(target.monto)}</span>}
          </div>
          <div style={{ color: "#aaa", marginTop: 8, fontSize: 10 }}>
            Se aplicará el mismo N° de factura a las {deliveries.length} entrega{deliveries.length === 1 ? "" : "s"}. El N° de guía queda preservado como audit en cada item.
          </div>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap: 10 }}>
          <Field label="Tipo de documento">
            <select
              value={docType} onChange={e => setDocType(e.target.value)}
              className="bini-input" style={{ height: 32 }} disabled={saving}
            >
              <option value="factura">Factura</option>
              <option value="boleta">Boleta</option>
            </select>
          </Field>
          <Field label="Fecha del DTE">
            <input
              type="date" className="bini-input" value={docDate} disabled={saving}
              onChange={e => setDocDate(e.target.value)}
            />
          </Field>
          <div style={{ gridColumn: "1 / -1" }}>
            <Field label={`N° ${docType} *`}>
              <input
                className="bini-input bini-mono" value={docNumber}
                onChange={e => setDocNumber(e.target.value)}
                placeholder="ej: 4567"
                autoFocus disabled={saving}
              />
            </Field>
          </div>
        </div>

        {progress && (
          <div style={{
            marginTop: 10, color:"#aaa", fontSize: 11,
            background:"#0d0d0d", padding:"6px 10px", borderRadius: 4,
            border:"1px solid #2e2e2e",
          }}>
            Procesando: {progress.done}/{progress.total}…
          </div>
        )}

        {error && (
          <div style={{
            color:"#e74c3c", fontSize: 11, marginTop: 10,
            background:"rgba(231,76,60,0.08)", padding:"6px 10px", borderRadius: 4,
            border:"1px solid rgba(231,76,60,0.3)",
          }}>{error}</div>
        )}

        <div style={{ display:"flex", justifyContent:"flex-end", gap: 8, marginTop: 14 }}>
          <button
            onClick={() => !saving && onClose?.()}
            disabled={saving}
            style={{
              background:"transparent", border:"1px solid #444",
              color:"#aaa", borderRadius: 5, padding:"7px 14px",
              fontSize: 12, cursor:"pointer",
            }}
          >Cancelar</button>
          <button
            onClick={submit}
            disabled={saving}
            style={{
              background:"#2ecc71", border:"none", color:"#0d2a15",
              borderRadius: 5, padding:"7px 16px",
              fontSize: 12, fontWeight: 700,
              cursor: saving ? "wait" : "pointer",
              opacity: saving ? 0.7 : 1,
            }}
          >{saving ? "Guardando…" : `Registrar ${docType}`}</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const Field = ({ label, children }) => (
  <div>
    <div style={{
      fontSize: 9.5, fontWeight: 700, color: "#cbd5e1",
      textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 3,
    }}>{label}</div>
    {children}
  </div>
);

export default FacturarGuiaCompletaModal;
